import React, { useEffect, useState } from 'react';
import { WBFlex, WBProgressBar, WBTypography } from '@admiin-com/ds-web';
import { useTranslation } from 'react-i18next';
import ConfirmationDlg from '../ConfirmationDlg/ConfirmationDlg';
import AdmiinLogo from '../AdmiinLogo/AdmiinLogo';

export interface PdfSigningConfirmationDlgProps {
  open: boolean;
  loading: boolean;
  actionedSignatures: number;
  totalSignatures: number;
  onConfirm: () => Promise<void>;
  onClose: () => void;
  showSigningMessage?: boolean;
}

const PdfSigningConfirmationDlg: React.FC<PdfSigningConfirmationDlgProps> = ({
  open,
  loading,
  actionedSignatures,
  totalSignatures,
  onConfirm,
  onClose,
  showSigningMessage = true,
}) => {
  const { t } = useTranslation();
  const [showUploadingMessage, setShowUploadingMessage] = useState(false);
  const [progress, setProgress] = useState(50);
  const showSigningProgress = loading && showSigningMessage;
  useEffect(() => {
    if (loading) {
      setShowUploadingMessage(false);
      setProgress(0);

      const messageTimer = setTimeout(() => {
        setShowUploadingMessage(true);
      }, 4500);

      // Fake progress animation
      const progressTimer = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 95) return 95;
          return prev + Math.random() * 10;
        });
      }, 500);

      return () => {
        clearTimeout(messageTimer);
        clearInterval(progressTimer);
      };
    }
  }, [loading]);

  return (
    <ConfirmationDlg
      open={open}
      title={
        showSigningProgress
          ? ''
          : t('confirmAllYourSignaturesSigned', { ns: 'taskbox' })
      }
      onOK={onConfirm}
      onClose={onClose}
      confirmText={t('confirmTitle', { ns: 'common' })}
      loading={loading}
    >
      <>
        {!showSigningProgress && (
          <WBTypography>
            {t('youSignedSignatures', {
              ns: 'taskbox',
              number: actionedSignatures,
              total: totalSignatures,
            })}
          </WBTypography>
        )}
        {showSigningProgress && (
          <>
            <WBFlex justifyContent="center" mb={3}>
              <AdmiinLogo />
            </WBFlex>
            <WBTypography textAlign="center" mb={1}>
              Signing the document, please don't close the browser
            </WBTypography>
            <WBProgressBar
              variant="determinate"
              value={Math.min(progress, 95)}
              sx={{
                height: 8,
                borderRadius: 4,
                width: '100%',
              }}
            />
            <WBFlex
              position="relative"
              justifyContent="center"
              mt={1}
              sx={{ minHeight: '20px' }}
            >
              <WBTypography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                sx={{
                  opacity: showUploadingMessage ? 0 : 1,
                  transition: 'opacity 0.5s ease-in-out',
                  visibility: showUploadingMessage ? 'hidden' : 'visible',
                }}
              >
                Applying your signature...
              </WBTypography>
              <WBTypography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                sx={{
                  opacity: showUploadingMessage ? 1 : 0,
                  transition: 'opacity 0.5s ease-in-out',
                  visibility: showUploadingMessage ? 'visible' : 'hidden',
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '100%',
                }}
              >
                Uploading the document...
              </WBTypography>
            </WBFlex>
          </>
        )}
      </>
    </ConfirmationDlg>
  );
};

export default PdfSigningConfirmationDlg;
